import { db } from "../firebase";
import { collection, getDocs, query, orderBy, where, addDoc, updateDoc, deleteDoc, doc, getDoc } from "firebase/firestore";
import { deleteEvent } from './eventService';
import { deletePayment } from './paymentService';
import { getCurrentUserPermission, getEffectiveUserId } from './userService';

const projectsCollection = collection(db, "projects");

// Vérifie la permission "projects" de l'utilisateur courant
async function assertProjectsAccess() {
  const perm = await getCurrentUserPermission("projects");
  return perm !== "none";
}

// Récupère tous les projets de l'utilisateur effectif
export const fetchProjects = async () => {
  if (!(await assertProjectsAccess())) return [];

  const userId = await getEffectiveUserId();

  const projectQuery = query(
    projectsCollection,
    where("userId", "==", userId),
    orderBy("order")
  );
  const querySnapshot = await getDocs(projectQuery);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [];
};

// Met à jour tous les projets (exemple d'utilisation)
export const updateAllProjects = async () => {
  if (!(await assertProjectsAccess())) return;

  const userId = await getEffectiveUserId();
  const projectQuery = query(projectsCollection, where("userId", "==", userId));
  const querySnapshot = await getDocs(projectQuery);
  const projects = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  for (const project of projects) {
    if (!project.id) continue;
    const projectRef = doc(db, "projects", project.id);
    await updateDoc(projectRef, { ...project, isFinished: project.isFinished || false });
  }
};

// Récupère les détails d'un projet par son ID
export const fetchProjectDetails = async (projectId) => {
  if (!(await assertProjectsAccess())) return null;

  const projectDoc = doc(db, "projects", projectId);
  const projectSnapshot = await getDoc(projectDoc);
  if (projectSnapshot.exists()) {
    return { id: projectSnapshot.id, ...projectSnapshot.data() };
  } else {
    throw new Error("Project not found");
  }
};

// Ajoute un nouveau projet pour l'utilisateur effectif
export const addProject = async () => {
  if (!(await assertProjectsAccess())) return null;

  const userId = await getEffectiveUserId();

  // Récupérer les projets de l'utilisateur pour déterminer l'ordre
  const projects = await fetchProjects();
  const newOrder = projects.length > 0 ? projects[projects.length - 1].order + 1 : 0;

  const newProject = {
    userId,
    name: "Nouveau projet",
    clientId: "",
    type: "",
    startDate: new Date().toString(),
    endDate: new Date().toString(),
    description: "",
    ressources: [],
    adress: "",
    city: "",
    postalCode: "",
    order: newOrder
  };

  const docRef = await addDoc(projectsCollection, newProject);
  return { id: docRef.id, ...newProject };
};

// Met à jour un projet existant
export const updateProject = async (project) => {
  if (!(await assertProjectsAccess())) return;

  if (!project || !project.id) {
    return;
  }
  const projectRef = doc(db, "projects", project.id);
  await updateDoc(projectRef, { ...project });
};

// Supprime un projet et ses dépendances
export const deleteProject = async (projectId) => {
  if (!(await assertProjectsAccess())) return;

  // Supprimer les paiements associés
  const paymentsQuery = query(collection(db, "payments"), where("projectId", "==", projectId));
  const paymentsSnapshot = await getDocs(paymentsQuery);
  for (const docSnap of paymentsSnapshot.docs) {
    await deletePayment(docSnap.id);
  }

  // Supprimer les événements associés
  const eventsQuery = query(collection(db, "events"), where("projectId", "==", projectId));
  const eventsSnapshot = await getDocs(eventsQuery);
  for (const docSnap of eventsSnapshot.docs) {
    await deleteEvent({ id: docSnap.id, ...docSnap.data() });
  }

  // Supprimer le projet lui-même
  const projectRef = doc(db, "projects", projectId);
  await deleteDoc(projectRef);
};